import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Calendar, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getForecastStatus } from '../utils/spendingStatus';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export default function ForecastPage() {
  const { user } = useAuth();
  const currency = user?.currency || 'GBP';
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchForecast = async () => {
    try {
      const response = await fetch(`${API}/forecast`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setForecast(data);
      }
    } catch (error) {
      toast.error('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
      </div>
    );
  }

  const chartData = forecast?.daily_forecast?.map((day) => ({
    name: day.day,
    balance: Math.max(day.balance, 0),
    bills: day.bills_due
  })) || [];

  const lowestBalance = forecast?.daily_forecast?.reduce((min, day) => 
    day.balance < min ? day.balance : min, 
    Infinity
  ) || 0;

  const hasRisk = lowestBalance < 0;
  
  // Get status using shared utility
  const status = getForecastStatus(forecast?.safe_to_spend_today, forecast?.days_until_payday);

  return (
    <div className="min-h-screen bg-[#0F1224] page-container" data-testid="forecast-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top">
        <h1 className="font-heading text-2xl font-bold text-white">Forecast</h1>
        <p className="text-[#6B7280]">Your money until payday</p>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-[#63E6E2]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Daily Budget</span>
            </div>
            <p className="font-heading text-2xl font-bold text-[#63E6E2]" data-testid="daily-budget">
              {formatCurrency(forecast?.safe_to_spend_today || 0, currency)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-[#2E93fA]" />
              <span className="text-[#6B7280] text-xs uppercase tracking-wider">Days Left</span>
            </div>
            <p className="font-heading text-2xl font-bold text-white" data-testid="days-remaining">
              {forecast?.days_until_payday || 0}
            </p>
          </motion.div>
        </div>

        {/* Risk Alert */}
        {hasRisk && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4"
          >
            <div className="flex items-start gap-3">
              <span className="text-xl">🔴</span>
              <div>
                <p className="text-white font-medium">Review needed</p>
                <p className="text-[#9CA3AF] text-sm mt-1">
                  Based on current bills, you may run short by {formatCurrency(Math.abs(lowestBalance), currency)} before payday.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Status Card - when not at risk */}
        {!hasRisk && forecast?.days_until_payday > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`${status.bgColor} border rounded-xl p-4`}
            style={{ borderColor: `${status.color}30` }}
          >
            <div className="flex items-start gap-3">
              <span className="text-xl">{status.emoji}</span>
              <div>
                <p className="text-white font-medium">{status.label}</p>
                <p className="text-[#9CA3AF] text-sm mt-1">{status.contextMessage}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <h3 className="font-heading text-lg font-bold text-white mb-4">Balance Forecast</h3>
          
          {chartData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#63E6E2" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#63E6E2" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="name" 
                    stroke="#6B7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#6B7280" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${getCurrencySymbol(currency)}${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#252A4A', 
                      border: '1px solid #2D3354',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => [`${formatCurrency(value, currency)}`, 'Balance']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#63E6E2" 
                    strokeWidth={2}
                    fill="url(#colorBalance)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-[#6B7280] text-center py-8">No forecast data available</p>
          )}
        </motion.div>

        {/* Daily Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <h3 className="font-heading text-lg font-bold text-white mb-4">Daily Breakdown</h3>
          
          <div className="space-y-2">
            {forecast?.daily_forecast?.slice(0, 10).map((day, index) => {
              const date = new Date(day.date);
              const isToday = index === 0;
              
              return (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isToday ? 'bg-[#63E6E2]/10 border border-[#63E6E2]/30' : 'bg-[#252A4A]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isToday ? 'bg-[#63E6E2]/20' : 'bg-[#2D3354]'
                    }`}>
                      <span className={`font-heading font-bold ${isToday ? 'text-[#63E6E2]' : 'text-white'}`}>
                        {date.getDate()}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {isToday ? 'Today' : day.day}
                      </p>
                      {day.bills_due > 0 && (
                        <p className="text-[#F59E0B] text-xs">
                          Bills: {formatCurrency(day.bills_due, currency)}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`font-semibold ${day.balance >= 0 ? 'text-white' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(day.balance, currency)}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav />
    </div>
  );
}
